import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Scissors, Calendar as CalendarIcon, Clock, User, AlertCircle, CheckCircle, XCircle, Loader2, ArrowLeft, RefreshCw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CancelPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [error, setError] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  const [rescheduled, setRescheduled] = useState(false);
  const [newDateTime, setNewDateTime] = useState(null);
  
  // Reschedule state
  const [showReschedule, setShowReschedule] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    fetchBookingInfo();
  }, [token]);

  const fetchBookingInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/cancel/${token}`);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Booking not found");
      }
      const data = await response.json();
      setBookingInfo(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (date) => {
    if (!bookingInfo?.booking?.barber_id) return;
    setLoadingSlots(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await fetch(`${API_URL}/api/slots/${bookingInfo.booking.barber_id}/${dateStr}`);
      if (response.ok) {
        const slots = await response.json();
        setAvailableSlots(slots.filter(s => s.available));
      }
    } catch (err) {
      console.error("Error fetching slots:", err);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    fetchAvailableSlots(date);
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/cancel/${token}`, {
        method: "POST",
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to cancel");
      }
      setCancelled(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) return;
    setRescheduling(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await fetch(`${API_URL}/api/bookings/reschedule/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: dateStr, time: selectedTime }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to reschedule");
      }
      const data = await response.json();
      setNewDateTime({ date: data.new_date, time: data.new_time });
      setRescheduled(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setRescheduling(false);
    }
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-zinc-400 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading booking information...</p>
        </div>
      </div>
    );
  }

  if (error && !bookingInfo) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Invalid Link</h1>
            <p className="text-zinc-400 mb-6">{error}</p>
            <Button onClick={() => navigate("/")} variant="outline" className="border-zinc-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Appointment Cancelled</h1>
            <p className="text-zinc-400 mb-6">
              Your appointment has been successfully cancelled. We hope to see you again soon!
            </p>
            <Button onClick={() => navigate("/")} className="bg-white text-black hover:bg-zinc-200">
              Book New Appointment
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (rescheduled) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Appointment Rescheduled!</h1>
            <p className="text-zinc-400 mb-4">Your appointment has been moved to:</p>
            <div className="bg-zinc-800 rounded-lg p-4 mb-6">
              <p className="text-white font-semibold text-lg">{newDateTime && formatDate(newDateTime.date)}</p>
              <p className="text-zinc-300">{newDateTime && formatTime(newDateTime.time)}</p>
            </div>
            <p className="text-zinc-500 text-sm mb-6">A confirmation SMS has been sent to your phone.</p>
            <Button onClick={() => navigate("/")} variant="outline" className="border-zinc-700 text-zinc-300">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const booking = bookingInfo?.booking;

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white tracking-wider mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            SHOWTIME SHOBI
          </h1>
          <p className="text-zinc-500 text-sm uppercase tracking-widest">Manage Appointment</p>
        </div>

        {/* Booking Details Card */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                <Scissors className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Service</p>
                  <p className="text-white font-medium">{booking?.service_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                <User className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Barber</p>
                  <p className="text-white font-medium">{booking?.barber_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                <CalendarIcon className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Date</p>
                  <p className="text-white font-medium">{booking?.date && formatDate(booking.date)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                <Clock className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Time</p>
                  <p className="text-white font-medium">{booking?.time && formatTime(booking.time)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Card */}
        {!bookingInfo?.can_cancel ? (
          <Card className="bg-red-950/20 border-red-900/50">
            <CardContent className="pt-6 pb-6">
              <div className="text-center">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">Cannot Modify</h2>
                <p className="text-zinc-400 text-sm mb-6">{bookingInfo?.message}</p>
                <Button onClick={() => navigate("/")} variant="outline" className="border-zinc-700 text-zinc-300">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : showReschedule ? (
          /* Reschedule View */
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6 pb-6">
              <div className="text-center mb-4">
                <RefreshCw className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                <h2 className="text-xl font-semibold text-white mb-1">Reschedule Appointment</h2>
                <p className="text-zinc-400 text-sm">Select a new date and time</p>
              </div>

              {/* Calendar */}
              <div className="flex justify-center mb-4">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => date < new Date()}
                  className="rounded-md border border-zinc-800 bg-zinc-900"
                />
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div className="mb-6">
                  <p className="text-zinc-400 text-sm mb-3 text-center">Available times for {selectedDate.toLocaleDateString()}</p>
                  {loadingSlots ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-center text-zinc-500 py-4">No available slots for this date</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => setSelectedTime(slot.time)}
                          className={`p-2 rounded-md text-sm font-medium transition-colors ${
                            selectedTime === slot.time
                              ? "bg-white text-black"
                              : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                          }`}
                        >
                          {formatTime(slot.time)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowReschedule(false);
                    setSelectedDate(null);
                    setSelectedTime(null);
                  }}
                  className="border-zinc-700 text-zinc-300"
                >
                  Back
                </Button>
                <Button
                  onClick={handleReschedule}
                  disabled={!selectedDate || !selectedTime || rescheduling}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="confirm-reschedule-btn"
                >
                  {rescheduling ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Rescheduling...
                    </>
                  ) : (
                    "Confirm New Time"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Initial Choice View */
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6 pb-6">
              <div className="text-center mb-6">
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">What would you like to do?</h2>
                <p className="text-zinc-400 text-sm">
                  You can reschedule to a different time or cancel your appointment.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => setShowReschedule(true)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12"
                  data-testid="reschedule-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reschedule Appointment
                </Button>
                
                <Button
                  onClick={handleCancel}
                  disabled={cancelling}
                  variant="outline"
                  className="w-full border-red-800 text-red-400 hover:bg-red-950/50 h-12"
                  data-testid="cancel-appointment-btn"
                >
                  {cancelling ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancel Appointment
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={() => navigate("/")}
                  variant="ghost"
                  className="w-full text-zinc-500 hover:text-zinc-300"
                >
                  Keep Current Appointment
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Display */}
        {error && bookingInfo && (
          <Card className="bg-red-950/20 border-red-900/50 mt-4">
            <CardContent className="pt-4 pb-4">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-zinc-600 text-xs mt-6">
          Need help? DM us on Instagram{" "}
          <a href="https://instagram.com/showtime_shobi" className="text-zinc-400 hover:text-white">
            @showtime_shobi
          </a>
        </p>
      </div>
    </div>
  );
}
